import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import type { Facet } from '../../../@types/ElasticSearchResponse';
import QueryFilter from '../../../helpers/query-filter';
import { getFacets, getQueryFilter, getSearchInitialized } from '../../../selectors/search';
import { H5 } from '../../../styled-components/components/Heading';
import LinkCloud from '../../../styled-components/components/LinkCloud';
import { getLocationSet } from '../../../utils/maps';

const url = (queryFilter: QueryFilter): string => queryFilter.removeQuery().toQueryURL();

interface ManufacturerLinkCloudProps {
  facets: Facet[];
}

const ManufacturerLinkCloud = ({ facets }: ManufacturerLinkCloudProps) => {
  const { t } = useTranslation('search');

  const manufacturers = facets
    ?.find((x) => x.id === 'manufacturer')
    ?.filters.sort((a, b) => b.count - a.count)
    .slice(0, 20);

  if (!manufacturers || !manufacturers.length) return null;

  return (
    <div data-wx-page="relative-top-thin">
      <div className="p-y-double border-top">
        <div className="container">
          <H5 as="h2">{t('New and used cars', { ns: 'search' })}</H5>
          <LinkCloud
            items={manufacturers.map((manufacturer) => ({
              text: manufacturer.displayName,
              title: t('Show new and used from this manufacturer', {
                ns: 'search',
                name: manufacturer.displayName,
              }),
              href: `/sok${url(QueryFilter.create(manufacturer.query))}`,
            }))}
          />
        </div>
      </div>
      <div className="p-y-double border-top">
        <div className="container">
          <H5 as="h2">{t('New and used cars in this country', { ns: 'search' })}</H5>
          <LinkCloud
            items={getLocationSet().map((position) => ({
              text: t('Cars in this location', { ns: 'search', name: position.name }),
              title: t('Show new and used cars in this location', {
                ns: 'search',
                name: position.name,
              }),
              href: `/sok${url(QueryFilter.create().addSpatial(position))}`,
            }))}
          />
        </div>
      </div>
    </div>
  );
};

interface ModelLinkCloudProps {
  facets: Facet[];
  queryFilter: QueryFilter;
}

const ModelLinkCloud = ({ facets, queryFilter }: ModelLinkCloudProps) => {
  const { t } = useTranslation('search');
  const manufacturer = queryFilter.get('manufacturer').pop();
  const models = facets
    ?.find((x) => x.id === 'modelSeries')
    ?.filters.sort((a, b) => b.count - a.count)
    .slice(0, 20);

  if (!models || !models.length) return null;

  return (
    <div data-wx-page="relative-top-thin">
      <div className="p-y-double border-top">
        <div className="container">
          <H5 as="h2">
            {t('New and used from this manufacturer', { ns: 'search', name: manufacturer })}
          </H5>
          <LinkCloud
            items={models.map((model) => ({
              text: `${manufacturer} ${model.displayName}`,
              title: t('Show new and used from this manufacturer', {
                ns: 'search',
                name: `${manufacturer} ${model.displayName}`,
              }),
              href: `/sok${url(
                QueryFilter.create()
                  .add('manufacturer', manufacturer)
                  .add('modelSeries', model.displayName),
              )}`,
            }))}
          />
        </div>
      </div>
      <div className="p-y-double border-top">
        <div className="container">
          <H5 as="h2">{t('Model in this country', { ns: 'search', name: manufacturer })}</H5>
          <LinkCloud
            items={getLocationSet().map((position) => ({
              text: `${manufacturer} i ${position.name}`,
              title: t('Show new and used cars from this manufacturer in this location', {
                ns: 'search',
                name: manufacturer,
                location: position.name,
              }),
              href: `/sok${url(
                QueryFilter.create().add('manufacturer', manufacturer).addSpatial(position),
              )}`,
            }))}
          />
        </div>
      </div>
    </div>
  );
};

const PrefixBase = (
  manufacturer: string | undefined,
  model: string | undefined,
  next: QueryFilter,
): [QueryFilter, string[]] => {
  const prefix: string[] = [];
  if (manufacturer) {
    next.add('manufacturer', manufacturer);
    prefix.push(`${manufacturer}`);
  }

  if (model) {
    next = next.add('modelSeries', model);
    prefix.push(model);
  }
  return [next, prefix];
};

const SearchLinkCloud = () => {
  const { t } = useTranslation('search');
  const visible = useSelector(getSearchInitialized);
  const _queryFilter = useSelector(getQueryFilter);
  const facets = useSelector(getFacets);

  if (!visible || facets.length === 0) {
    return null;
  }

  const displayModelYears = !_queryFilter.hasRange('modelYear');

  let queryFilter = _queryFilter;

  queryFilter = queryFilter.removeSpatial().removeRange('modelYear');

  if (queryFilter.numberOfFilters() === 0) return <ManufacturerLinkCloud facets={facets} />;

  if (queryFilter.numberOfFilters() === 1 && queryFilter.has('manufacturer'))
    return <ModelLinkCloud facets={facets} queryFilter={queryFilter} />;

  if (!queryFilter.has('manufacturer') && !queryFilter.has('modelSeries')) return null;

  const manufacturer =
    queryFilter.get('manufacturer').pop() ||
    facets?.find((x) => x.id === 'manufacturer')?.filters?.sort((a, b) => b.count - a.count)?.[0]
      ?.displayName;

  const model = queryFilter.get('modelSeries').pop();

  const boosts = ['gearboxType', 'drivingWheel', 'fuelType', 'properties.segment'];
  const filters = ([] as any[])
    .concat(
      ...facets
        ?.filter((x) => boosts.includes(x.id))
        .map((x) => x.filters.map((y) => ({ ...y, id: x.id }))),
    )
    .sort((a, b) => b.count - a.count)
    .slice(0, 20);

  const years = facets
    ?.find((x) => x.id === 'modelYear')
    ?.filters.sort(
      (a, b) =>
        Number.parseInt(b.displayName.replace(/</g, ''), 10) -
        Number.parseInt(a.displayName.replace(/</g, ''), 10),
    )
    .slice(0, 8)
    .map((x) => x.displayName);

  return (
    <div data-wx-page="relative-top-thin">
      <div className="p-y-double border-top">
        <div className="container">
          <H5 as="h2">
            {t('New and used from this manufacturer', {
              ns: 'search',
              name: manufacturer ? `${manufacturer} ${model}` : model,
            })}
          </H5>
          <LinkCloud
            items={filters.map((filter) => {
              const qf = QueryFilter.create().add(filter.id, filter.displayName);
              const [next, prefix] = PrefixBase(manufacturer, model, qf);

              prefix.push(filter.displayName);

              return {
                text: prefix.join(' '),
                title: t('Show new and used from this manufacturer', {
                  ns: 'search',
                  name: prefix.join(' '),
                }),
                href: `/sok${url(next)}`,
              };
            })}
          />
        </div>
      </div>
      {displayModelYears && (
        <div className="p-y-double border-top">
          <div className="container">
            <H5 as="h2">
              {t('Model model years', {
                ns: 'search',
                name: manufacturer ? `${manufacturer} ${model}` : model,
              })}
            </H5>
            <LinkCloud
              items={
                years?.map((year) => {
                  const qf = QueryFilter.create().addRange(
                    'modelYear',
                    Number.parseInt(year, 10),
                    Number.parseInt(year, 10),
                  );

                  const [next, prefix] = PrefixBase(manufacturer, model, qf);

                  return {
                    text: t('modelFromYear', {
                      ns: 'search',
                      model: prefix.join(' '),
                      year,
                    }),
                    title: t('Show new and used cars of this model from this year', {
                      ns: 'search',
                      name: prefix.join(' '),
                      year,
                    }),
                    href: `/sok${url(next)}`,
                  };
                }) || []
              }
            />
          </div>
        </div>
      )}
      <div className="p-y-double border-top">
        <div className="container">
          <H5 as="h2">
            {t('Model in this country', {
              ns: 'search',
              name: manufacturer ? `${manufacturer} ${model}` : model,
            })}
          </H5>
          <LinkCloud
            items={getLocationSet().map((position) => {
              const qf = QueryFilter.create().addSpatial(position);

              const [next, prefix] = PrefixBase(manufacturer, model, qf);

              return {
                text: `${prefix.join(' ')} i ${position.name}`,
                title: t('Show new and used cars from this manufacturer in this location', {
                  ns: 'search',
                  name: prefix.join(' '),
                  location: position.name,
                }),
                href: `/sok${url(next)}`,
              };
            })}
          />
        </div>
      </div>
    </div>
  );
};

export default SearchLinkCloud;
